import { Component } from 'preact';
import style from './styles/RaffleDetail.scss';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import RaffleService from '../../services/RaffleService';
import RaffleList from '../../components/raffle/RaffleList';
import RaffleRepository from '../../repositories/RaffleRepository';
import RouteHelper from '../../helpers/RouteHelper';
import { fetchingRaffleAction, fetchedRaffleAction, clearFetchedRaffleAction } from '../../actions/RaffleDetailActions';
import LoadingSpinner from '../../components/spinner/LoadingSpinner';
import RaffleBanner from '../../components/raffle/RaffleBanner';
import HeaderArea from '../../components/blocks/raffleDetail/HeaderArea';
import MainArea from '../../components/blocks/raffleDetail/MainArea';
import RegistrationArea from '../../components/blocks/raffleDetail/RegistrationArea';
import ScrollableAnchor from 'react-scrollable-anchor';
import Layout from '../../components/Layout';
import PageNotFound from '../../components/PageNotFound';
import MutationService from '../../services/MutationService';
import MetaTags from 'react-meta-tags';
import Promo from '../../components/promo/Promo';

/**
 * @property {Raffle} props.raffle
 * @property {boolean} props.raffleNotFound
 * @property {boolean} props.fetching
 */
// eslint-disable-next-line react/prefer-stateless-function
class RaffleDetail extends Component {

	static fetchData(store, match) {
		const { id } = RouteHelper.parseSlugAndId(match.params.slugAndId);
		if (id) {
			return [
				store.dispatch(RaffleRepository.fetchRaffle(id, fetchingRaffleAction, fetchedRaffleAction)),
				store.dispatch(RaffleRepository.fetchActiveOrComingSoon()),
			];
		}
		// This will result in 404 (raffleNotFound=true)
		store.dispatch(fetchedRaffleAction(null));
		return [];
	}

	static getHtmlHeadFragment(store, url) {
		if(!store) {
			return '<meta name="robots" content="index, follow">';
		}

		const mutationCode = MutationService.getMutationCode();
		const raffle = store.getState().raffleDetail.raffle;
		const title = `${raffle.getTitle(mutationCode)} - ${raffle.code} - ${raffle.getSubtitle(mutationCode)}`;
		const description = raffle.getDescription(mutationCode);

		let images = ``;
		raffle.images.map(image => images += `<meta property="og:image" content="${RaffleService.getImageUrl(image)}"/>`);

		return `<title>${title} - Footshop - Releases</title>` +
				`<meta name="description" content="${description}"/>` +
				`<meta name="robots" content="index, follow">` +
				`<meta property="og:title" content="${title} | Footshop"/>` +
				`<meta property="og:url" content="https://releases.footshop.com${url}" ` +
				`<meta property="og:description" content="${description}"/>` +
				`${images}`;
	}

	/**
	 * @param {?string} id
	 */
	fetchRaffleIfRequired = (id) => {
		if (!this.props.raffle || id !== this.props.raffle.id) {
			if (this.props.raffle) {
				this.props.clearFetchedRaffleAction(null);
			}
			// If we received id AND we have no raffle loaded OR loaded raffle id has different id
			this.props.fetchRaffle(id, fetchingRaffleAction, fetchedRaffleAction);
		}
	};

	/**
	 * Note that fetch initial data is handled separately in server.js
	 */
	componentDidMount() {
		const {fetchActiveOrComingSoon, activeOrComingSoonRaffles} = this.props;
		const { id } = RouteHelper.parseSlugAndId(this.props.match.params.slugAndId);
		this.fetchRaffleIfRequired(id);

		if(activeOrComingSoonRaffles.items.length === 0) {
			fetchActiveOrComingSoon();
		}
	}

	componentWillReceiveProps(nextProps, nextContext) {
		const oldSlugAndId = this.props.match.params.slugAndId;
		const newSlugAndId = nextProps.match.params.slugAndId;
		if (oldSlugAndId !== newSlugAndId) {
			const { id } = RouteHelper.parseSlugAndId(newSlugAndId);
			this.fetchRaffleIfRequired(id);
		}
	}

	render({ route, location, raffle, raffleNotFound, fetching, fetchActiveOrComingSoon, activeOrComingSoonRaffles, suggestedShopId }) {
		if (raffleNotFound) {
			return <PageNotFound {...{ route, location }} />;
		}

		if (!raffle) {
			return (
				<Layout {...{ route, location }}>
					<div className="abs-vertical-center">
						<LoadingSpinner active />
					</div>
				</Layout>
			);
		}
		const mutationCode = MutationService.getMutationCode();
		const { id } = RouteHelper.parseSlugAndId(this.props.match.params.slugAndId);
		const activeRaffles = activeOrComingSoonRaffles.items.length !== 0 ?
			{
				...activeOrComingSoonRaffles,
				items: activeOrComingSoonRaffles.items.filter(item => item.id !== id)
			} : null;

		return (
			<Layout {...{ route, location }} >
				<MetaTags>
					<title>{raffle.getTitle(mutationCode)} - {raffle.code} - {raffle.getSubtitle(mutationCode)} - {route.metaTitle}</title>
					<meta name="description" content={`${raffle.getDescription(mutationCode)}`}/>
				</MetaTags>
				<RaffleBanner raffle={raffle} />
				<div className="container">
					<ScrollableAnchor id={'detail'}>
						<div className={`row ${style['header-row']}`}>
							<div className={`col-sm-12 ${style['header-area']}`}>
								<HeaderArea raffle={raffle} className={style['ie-flexbox-fix']} />
							</div>
							<div className={`col-sm-12 ${style['registration-area']}`}>
								<RegistrationArea
									raffle={raffle}
									suggestedShopId={suggestedShopId}
									className={style['ie-flexbox-fix']}
								/>
							</div>
						</div>
					</ScrollableAnchor>
					<div className="row">
						<div className={`col-sm-12`}>
							<MainArea raffle={raffle} lang={mutationCode}/>
						</div>
					</div>

					{(activeRaffles && activeRaffles.items.length !== 0)
						? (
							<div className={style['active-raffles']}>
								<h2>Opened raffles</h2>
								<div className={style.divider} />
								<RaffleList
									{...activeRaffles}
									fetchMore={fetchActiveOrComingSoon}
									hasPromo
									suggestedShopId={suggestedShopId}
								/>
							</div>
						) : (
							<Promo
								big
								suggestedShopId={suggestedShopId}
							/>
						)
					}
				</div>
			</Layout>
		);
	}
}

const mapStateToProps = (state) => {
	const { raffleDetail, homepage } = state;

	return {
		raffle: raffleDetail.raffle,
		raffleNotFound: raffleDetail.raffleNotFound,
		fetching: raffleDetail.fetching,
		activeOrComingSoonRaffles: homepage.activeOrComingSoonRaffles,
		suggestedShopId: state.ui.suggestedShopId
	};
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
	fetchRaffle: RaffleRepository.fetchRaffle,
	fetchActiveOrComingSoon: RaffleRepository.fetchActiveOrComingSoon,
	clearFetchedRaffleAction
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(RaffleDetail);



// WEBPACK FOOTER //
// ./routes/raffle/RaffleDetail.js